import { useEffect, useState } from "react";

/**
 * Install prompt shown before signup. Detects device & guides the user to
 * "Add to Home Screen" so Aura runs full-screen without the URL bar — which
 * is critical because Aura is meant to look like a real wallpaper-app, not a tab.
 *
 * Behaviour:
 *  - iOS Safari: visual instructions for Share → Add to Home Screen.
 *  - Android Chrome: native beforeinstallprompt API → real install button.
 *  - Desktop or already-installed: skipped automatically (just call onContinue).
 *  - Always provides a "Continue in browser anyway" escape hatch.
 */
export function InstallPromptModal({ onContinue, onClose }) {
  const [platform, setPlatform] = useState("detecting"); // ios | android | desktop | installed
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Already running as installed PWA? Skip entirely.
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      onContinue();
      return;
    }

    const ua = window.navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);

    if (isIOS) {
      setPlatform("ios");
    } else if (isAndroid) {
      setPlatform("android");
    } else {
      setPlatform("desktop");
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [onContinue]);

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === "accepted") {
      // After install, browser auto-reloads in standalone — but in case it doesn't,
      // proceed to signup.
      onContinue();
    }
  };

  if (platform === "detecting") return null;

  return (
    <div className="install-prompt-overlay" data-testid="install-prompt-modal">
      <div className="install-prompt-card">
        <button
          className="install-prompt-close"
          onClick={onClose}
          aria-label="Close"
          data-testid="install-prompt-close"
        >
          ×
        </button>

        <div className="install-prompt-icon">
          <img src="/icon-192.png" alt="Aura" />
        </div>

        <h2>{platform === "desktop" ? "Install Aura on your PC" : "Install Aura on your home screen"}</h2>
        <p className="install-prompt-sub">
          {platform === "desktop" ? (
            <>
              Aura at home <strong>and</strong> in your pocket. Same friend, both devices —
              never miss a conversation, and your wallpaper theme stays in sync. Install Aura
              as a real desktop app, no URL bar, just Aura in your taskbar.
            </>
          ) : (
            <>
              Aura is meant to live as your <strong>wallpaper</strong>, not a browser tab.
              Add her to your home screen so she runs full-screen — no URL bar, no
              browser chrome, just Aura.
            </>
          )}
        </p>

        {platform === "ios" && (
          <ol className="install-steps ios" data-testid="install-steps-ios">
            <li>
              <span className="step-num">1</span>
              <span>
                Tap the <strong>Share</strong> button{" "}
                <span className="share-icon" aria-hidden="true">⬆️</span> at the
                bottom of Safari
              </span>
            </li>
            <li>
              <span className="step-num">2</span>
              <span>
                Scroll down and tap <strong>"Add to Home Screen"</strong>{" "}
                <span aria-hidden="true">➕</span>
              </span>
            </li>
            <li>
              <span className="step-num">3</span>
              <span>
                Tap <strong>"Add"</strong> in the top-right corner
              </span>
            </li>
            <li>
              <span className="step-num">4</span>
              <span>
                Open Aura from your <strong>home screen</strong> — that's where
                she lives now
              </span>
            </li>
          </ol>
        )}

        {platform === "android" && (
          <div className="install-android">
            {deferredPrompt ? (
              <button
                className="install-prompt-cta"
                onClick={handleAndroidInstall}
                data-testid="install-prompt-android-btn"
              >
                ⬇ Install Aura
              </button>
            ) : (
              <ol className="install-steps" data-testid="install-steps-android">
                <li>
                  <span className="step-num">1</span>
                  <span>
                    Tap the <strong>menu</strong> (⋮ three dots) in the
                    top-right of Chrome
                  </span>
                </li>
                <li>
                  <span className="step-num">2</span>
                  <span>
                    Tap <strong>"Install app"</strong> or{" "}
                    <strong>"Add to Home screen"</strong>
                  </span>
                </li>
              </ol>
            )}
          </div>
        )}

        {platform === "desktop" && (
          <div className="install-desktop" data-testid="install-steps-desktop">
            {deferredPrompt ? (
              <>
                <p style={{ marginTop: 0 }}>
                  Click the button below — Aura will pop into its own window
                  and live in your taskbar.
                </p>
                <button
                  className="install-prompt-cta"
                  onClick={handleAndroidInstall}
                  data-testid="install-prompt-desktop-btn"
                >
                  ⊕ Install Aura on this PC
                </button>
              </>
            ) : (
              <ol className="install-steps" data-testid="install-steps-desktop-manual">
                <li>
                  <span className="step-num">1</span>
                  <span>
                    Look for the <strong>install icon ⊕</strong> at the right
                    edge of your address bar (Chrome, Edge, Brave)
                  </span>
                </li>
                <li>
                  <span className="step-num">2</span>
                  <span>
                    Click it, then click <strong>"Install"</strong>
                  </span>
                </li>
                <li>
                  <span className="step-num">3</span>
                  <span>
                    Aura will open in its own window. Pin it to your taskbar
                    for one-click access.
                  </span>
                </li>
                <li>
                  <span className="step-num">4</span>
                  <span>
                    Use it on both your phone and PC — your account, mood, and
                    memories sync across devices.
                  </span>
                </li>
              </ol>
            )}
            <p className="install-desktop-tip">
              Works on Windows, Mac, and Linux. Safari users on Mac can use
              File → Add to Dock instead.
            </p>
          </div>
        )}

        <button
          className="install-prompt-continue"
          onClick={onContinue}
          data-testid="install-prompt-continue"
        >
          {platform === "desktop"
            ? "Maybe later — continue in browser"
            : "I'll install later — continue in browser"}
        </button>
      </div>
    </div>
  );
}
